/**
 * CTC master data (customers, customer groups, brokers, commodities, currencies, commission
 * profiles, bank accounts): which records may be used for something new.
 *
 * - retired: hidden for now, may come back.
 * - deleted: soft delete, hidden for good. The document is kept so anything already referencing
 *   it by id (orders, PDFs, commission calculation, WhatsApp) keeps working.
 *
 * Neither can be picked for a new order or a purchase; the server enforces this too.
 */
export type CtcMasterRecord = { retired?: boolean; deleted?: boolean } | undefined | null;

export const isCtcDeleted = (record: CtcMasterRecord): boolean => record?.deleted === true;

/** Exists, not retired and not deleted. */
export const isCtcActive = (record: CtcMasterRecord): boolean => !!record && record.retired !== true && record.deleted !== true;

/**
 * Why a referenced record can't be used for something new, or undefined when it can.
 * `label` names it in the message, e.g. "Commission profile".
 */
export const ctcInactiveReason = (record: CtcMasterRecord, label: string): string | undefined => {
    if (!record) return `${label} does not exist`;
    if (record.deleted === true) return `${label} was deleted`;
    if (record.retired === true) return `${label} is retired`;
    return undefined;
};

/**
 * A new order may only use a commission profile linked to its customer
 * (ctc_customers.commissionIds). Undefined when it may.
 */
export const ctcCommissionNotLinkedReason = (customer: { commissionIds?: string[] } | undefined, commissionId: string): string | undefined =>
    customer?.commissionIds?.includes(commissionId) ?
        undefined :
        "This commission profile is not linked to the customer. Choose one of the customer's commission profiles.";

/** Values a dynamic field (ctc_customers/{id}/dynamic_fields) can hold on an order. */
export type CtcDynamicFieldValue = string | number | boolean;

type CtcDynamicFieldLabel = { name_en?: string; name_ar?: string; order?: number };

/**
 * An order's dynamic fields as [label, value] rows, in back office order, using the labels copied onto
 * the order when it was saved (so renaming or deleting a field later doesn't change old orders). Orders
 * saved before labels were copied use the customer's current fields (`currentFields`, by id), else the id.
 */
export const ctcDynamicFieldRows = (
    po: { dynamicFields?: Record<string, CtcDynamicFieldValue>; dynamicFieldDefs?: Record<string, CtcDynamicFieldLabel> },
    language: "en" | "ar" = "en",
    currentFields?: Record<string, CtcDynamicFieldLabel>,
): [string, string][] =>
    Object.entries(po.dynamicFields ?? {})
        .map(([id, value]) => ({ id, value, def: po.dynamicFieldDefs?.[id] ?? currentFields?.[id] }))
        .sort((a, b) => (a.def?.order ?? 0) - (b.def?.order ?? 0))
        .map(({ id, value, def }) => [
            (language === "ar" ? def?.name_ar || def?.name_en : def?.name_en || def?.name_ar) || id,
            typeof value === "boolean" ? (language === "ar" ? (value ? "نعم" : "لا") : value ? "Yes" : "No") : String(value),
        ]);
